import {
  ApiErrorBodySchema,
  buildPath,
  routes,
  type RouteBody,
  type RouteName,
  type RouteParams,
  type RouteQuery,
  type RouteResponse,
} from '@de/shared'

/**
 * Typed fetch client over the shared route table (`@de/shared` routes.ts).
 *
 *   const { election } = await api('getElection', { params: { id } })
 *   await api('createElection', { body: { title: 'My vote' } })
 *   await api('registrationStatus', { params: { slug }, headers: registrationHeaders(slug) })
 *
 * - Same-origin requests with cookies (organizer / voter sessions are httpOnly cookies).
 * - Non-2xx ⇒ throws ApiError { status, code, message, details } parsed from the shared ApiErrorBody.
 * - 2xx bodies are parsed with the route's response schema (defaults applied, contract drift surfaced).
 */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

export const isApiError = (err: unknown, code?: string): err is ApiError =>
  err instanceof ApiError && (code === undefined || err.code === code)

type IsNever<T> = [T] extends [never] ? true : false

export type ApiCallOptions<N extends RouteName> = (IsNever<RouteParams<N>> extends true
  ? { params?: undefined }
  : { params: RouteParams<N> }) &
  (IsNever<RouteBody<N>> extends true ? { body?: undefined } : { body: RouteBody<N> }) &
  (IsNever<RouteQuery<N>> extends true ? { query?: undefined } : { query?: RouteQuery<N> }) & {
    headers?: Record<string, string>
    signal?: AbortSignal
  }

type OptionalIfEmpty<N extends RouteName> =
  IsNever<RouteParams<N>> extends true
    ? IsNever<RouteBody<N>> extends true
      ? [options?: ApiCallOptions<N>]
      : [options: ApiCallOptions<N>]
    : [options: ApiCallOptions<N>]

export function toQueryString(query: Record<string, unknown> | undefined): string {
  if (!query) return ''
  const sp = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue
    sp.set(key, Array.isArray(value) ? value.join(',') : String(value))
  }
  const s = sp.toString()
  return s ? `?${s}` : ''
}

export async function api<N extends RouteName>(name: N, ...[options]: OptionalIfEmpty<N>): Promise<RouteResponse<N>> {
  const route = routes[name]
  const opts = (options ?? {}) as ApiCallOptions<N>
  const url =
    buildPath(route.path, (opts.params ?? {}) as Record<string, string | number>) +
    toQueryString(opts.query as Record<string, unknown> | undefined)

  const hasBody = opts.body !== undefined
  const res = await fetch(url, {
    method: route.method,
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
      ...opts.headers,
    },
    body: hasBody ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  })

  const text = await res.text()
  let json: unknown = undefined
  if (text) {
    try {
      json = JSON.parse(text)
    } catch {
      json = undefined
    }
  }

  if (!res.ok) {
    const parsed = ApiErrorBodySchema.safeParse(json)
    if (parsed.success) {
      const { code, message, details } = parsed.data.error
      throw new ApiError(res.status, code, message, details)
    }
    throw new ApiError(res.status, res.status === 404 ? 'not_found' : 'http_error', res.statusText || 'Request failed')
  }

  const parsed = route.response.safeParse(json)
  if (!parsed.success) {
    if (import.meta.env.DEV) {
      console.error(`[api] ${name}: response does not match the shared contract`, parsed.error.issues, json)
      throw new ApiError(
        res.status,
        'contract_mismatch',
        `Unexpected response from ${route.method} ${route.path}`,
        parsed.error.issues
      )
    }
    return json as RouteResponse<N>
  }
  return parsed.data as RouteResponse<N>
}
